import { assertValidRegistry } from "@/data/showcaseValidation";

// Production media origin (Cloudflare R2 public bucket). Absolute URLs only; no MP4s in Git.
export const MEDIA_ORIGIN = "https://media.katlastechnology.com";
export const media = (p) => `${MEDIA_ORIGIN}/${String(p).replace(/^\/+/, "")}`;

// Section-level copy (kept out of the component).
export const SECTOR_SECTION = {
  index: "§ 04",
  eyebrow: "Operational proofs",
  heading:
    "One reusable KATLAS engineering architecture, shown in four operational contexts — not four separate products.",
  supporting:
    "Finance, Health, Logistics & Infrastructure and Passenger are four proofs of one reusable engineering method. For each, see the problem holder's decision, the decisive freeze / repair / re-check moment, and the approved film.",
  governingLine:
    "Authorised roles decide. KATLAS makes the boundary executable and proves the outcome.",
};

// One structured registry. All sector content + media metadata lives here.
// Order is deliberate: Finance → Health → Logistics → Passenger.
// mediaStatus: awaiting-media | approved | archived
// deploymentStatus: showcase | live-katlas-node | simulation | concept | archived
export const SECTOR_JOURNEYS = [
  {
    id: "finance",
    label: "Finance",
    title: "Govern an asset transfer before execution",
    shortDescription:
      "Govern an asset action before the execution system proceeds.",
    problem:
      "Value movement and standards change cross institutional boundaries.",
    governanceMoment:
      "The journey freezes, responsible evidence repair occurs and execution is gated by the resulting control decision.",
    evidenceUrl: "/technical-appendix",
    videoUrl: "/media/finance-control-envelope.mp4",
    posterUrl: "/media/finance-plate.jpg",
    captionsUrl: null,
    transcript: [
      "Transfer requested",
      "Custody and role established",
      "Authority checked",
      "Policy and asset state tested",
      "Verified outcome consumed",
      "Receipt",
    ],
    destinationUrl: "https://controlcierge.katlastechnology.com",
    stakeholders: [
      "Custodian",
      "Transfer agent",
      "Authorising role",
      "Register / downstream system",
    ],
    governedAction:
      "Execute an asset transfer only after custody, role, authority and asset-state conditions are verified; the downstream register consumes the verified outcome.",
    outcomes: ["Allowed", "Released", "Blocked"],
    proofSummary:
      "A public-safe receipt proves the transfer outcome and authority context, without exposing the underlying asset or party data.",
    deploymentStatus: "showcase",
    mediaStatus: "approved",
  },
  {
    id: "health",
    label: "Health",
    title: "Release a standards change safely",
    shortDescription:
      "Release a change safely across a regulated service network.",
    problem:
      "Patient consent, professional contributions, service acceptance and follow-up ownership sit across different organisations.",
    governanceMoment:
      "A capacity exception remains visible until it is repaired and the complete journey is re-checked.",
    evidenceUrl: "/technical-appendix",
    videoUrl: "/media/health-control-envelope.mp4",
    posterUrl: "/media/health-plate.jpg",
    captionsUrl: "/media/health-control-envelope.vtt",
    transcript: [
      "Change published",
      "Local impact assessed",
      "Installation evidence referenced",
      "Authorised service review",
      "Release or exception",
      "Receipt",
    ],
    destinationUrl: "https://change-release.katlastechnology.com",
    stakeholders: [
      "Standards authority",
      "Local health service",
      "Installer / evidence custodian",
      "Authorised service reviewer",
    ],
    governedAction:
      "Release a published standards change into a local service — or route an exception — once installation evidence and an authorised service review are satisfied.",
    outcomes: ["Released", "Review required", "Refused"],
    proofSummary:
      "A public-safe receipt proves the release or exception: who reviewed, under what authority, which evidence was referenced, and what was withheld.",
    deploymentStatus: "showcase",
    mediaStatus: "approved",
  },
  {
    id: "logistics",
    label: "Logistics & Infrastructure",
    title: "SupportCierge — the Connected Home Handoff",
    shortDescription:
      "Prove a new home broadband service is genuinely ready end to end — not just locally 'done'.",
    problem:
      "A customer switches to a new broadband service in a refitted home. Every commercial system reports 'complete' — yet no single system holds proof the home is actually connected and working end to end.",
    governanceMoment:
      "Physical-premises readiness has quietly failed. The KATLAS Node freezes the handoff, names the field engineer accountable for the repair, and records a signed receipt of exactly why.",
    evidenceUrl: "/technical-appendix",
    videoUrl: "/media/supportcierge-connected-home.mp4",
    posterUrl: "/media/logistics-plate.jpg",
    captionsUrl: null,
    transcript: [
      "Switch accepted — every local system reports complete",
      "The home is not actually ready — physical-premises readiness has failed",
      "The KATLAS Node freezes the handoff and names the accountable role",
      "Responsible repair across physical and digital boundaries",
      "Complete re-check — all four gates pass together",
      "Service-ready, released and closed — with a signed, verifiable receipt",
    ],
    // No public demonstrator URL supplied yet. Do not fabricate one.
    destinationUrl: null,
    stakeholders: [
      "Switching & order service",
      "Network & premises readiness",
      "Field engineer",
      "Equipment & activation service",
    ],
    governedAction:
      "Release the connected-home service only when service identity, physical premises, equipment activation and end-to-end readiness pass together — re-checked as one service instance.",
    outcomes: ["Frozen", "Repair required", "Released"],
    proofSummary:
      "A signed, inspectable decision receipt (simulated in this demonstrator) proves end-to-end service readiness — which conditions were satisfied and by whom — verifiable by its SHA-256 digest, without centralising any role's evidence.",
    deploymentStatus: "showcase",
    mediaStatus: "approved",
  },
  {
    id: "passenger",
    label: "Passenger",
    title: "PassengerCierge — an assisted-travel decision during disruption",
    shortDescription:
      "Protect an assisted passenger in disruption by acting only on trusted, attributable evidence.",
    problem:
      "During a disruption, advisories arrive from many sources — operator, station staff, network status, and unverified third parties or social media. A passenger who needs assistance must not be re-routed on unsafe or unattributable information.",
    governanceMoment:
      "Unverified and social-media advisories are quarantined at the boundary; the journey freezes on an unsafe branch, a safe re-accommodation is proven against the passenger's assistance needs, and the decision is sealed as a signed receipt.",
    evidenceUrl: "/technical-appendix",
    videoUrl: "/media/passenger-walkthrough.mp4",
    posterUrl: "/media/passenger-plate.jpg",
    captionsUrl: null,
    transcript: [
      "Disruption detected on the passenger's booked journey",
      "Advisories gathered and classified by trust level",
      "Unverified third-party and social-media inputs quarantined",
      "Trusted operator, network and station-staff evidence preserved",
      "A safe alternative is proven against the passenger's assistance needs",
      "Signed decision receipt — who decided, on what evidence, and what was excluded",
    ],
    // Separate interactive sandbox, deployed on its own subdomain. Link out (no iframe).
    destinationUrl: "https://passengercierge.katlastechnology.com",
    stakeholders: [
      "Assisted passenger",
      "Rail operator",
      "Station & network status",
      "Trusted advisory sources",
    ],
    governedAction:
      "Recommend and re-accommodate an assisted journey only from trusted, attributable evidence — excluding unverified advisories — and seal the outcome with a signed, inspectable decision receipt.",
    outcomes: ["Allowed", "Frozen", "Re-accommodated"],
    proofSummary:
      "A public-safe decision receipt (simulated in this demonstrator) proves which evidence was trusted, what was excluded, and under whose authority the assisted-travel decision was made — verifiable by its hash, without centralising the passenger's private data.",
    deploymentStatus: "simulation",
    mediaStatus: "approved",
  },
];

// Fail fast on misconfiguration (secrets, data URLs, dev hosts, active video without
// transcript, unauthorised live-katlas-node). The public site never enables live-katlas-node.
assertValidRegistry(SECTOR_JOURNEYS, { liveNodeApproved: false });
